import { action, computed, makeObservable, observable } from 'mobx';
import { ICategoryWithProducts, IMenuParams, IMenuProduct, IResponseProduct } from '../../types/products';
import { requests } from 'src/requests';
import client from 'src/services/client';
import { IBranch } from 'src/types/branches';
import { debounce } from 'src/utils/perfomance';
import logger from '~/services/logger';
import { productUtils } from '~/utils/productUtils';

export class MenuStore {
  constructor() {
    makeObservable(this);
  }

  @observable public loading: boolean = false;
  @observable public favorites: boolean = false;
  @observable public isSearchActive: boolean = false;
  @observable public categoryId: ICategoryWithProducts['id'] | undefined = undefined;
  @observable public parameters: IMenuParams = {};
  @observable public categories: ICategoryWithProducts[] = [];
  @observable public favoriteProducts: IMenuProduct[] = [];
  @observable public branch: IBranch | undefined;
  private cache = new Map<number, { categories: any[]; menu_update: number; global_category_id: number }>();

  @computed get category(): ICategoryWithProducts | null {
    if (!this.categoryId) return null;
    const categoryIdx = this.categories.findIndex((category) => category.id === this.categoryId);
    return this.categories[categoryIdx];
  }

  @action public setCategory = (id?: ICategoryWithProducts['id']) => {
    try {
      if (!id) this.categoryId = undefined;
      const categoryIdx = this.categories.findIndex((category) => category.id === id);
      if (~categoryIdx) this.categoryId = this.categories[categoryIdx].id;
    } catch (error: any) {
      logger.error({ error, place: 'MenuStore setCategory' });
    }
  };

  @action public changeSearchActiveStatus = (value: boolean) => {
    this.isSearchActive = value;
  };

  @action public setParameters = (params: IMenuParams) => {
    try {
      this.parameters = params;
      this.callFetchMenu();
    } catch (error: any) {
      logger.error({ error, place: 'MenuStore setParameters' });
    }
  };
  @action public changeFavoritesVisibility = (visible: boolean) => {
    try {
      this.favorites = visible;
    } catch (error: any) {
      logger.error({ error, place: 'MenuStore changeFavoritesVisibility' });
    }
  };
  @action public fetchMenu = async (parameters?: IMenuParams & { customerId?: number }) => {
    try {
      this.loading = true;

      if (parameters) {
        const { branch, ...rest_parameters } = parameters;

        this.branch = branch;
        parameters = { ...this.parameters, ...rest_parameters, branch_id: branch?.id };
      } else {
        parameters = { ...this.parameters };
      }

      const branchId = parameters.branch_id;

      if (parameters.customerId) {
        this.fetchFavoriteProduct(parameters.customerId);
      }
      // const hasUpdates = this.hasMenuUpdate(cashedParameters);
      // if (branch_id) this.parameters['branch_id'] = branch_id;
      // if (!hasUpdates) {
      //   // do not show loader if menu does not have updates
      //   // need to make silent update
      //   this.loading = false;
      //   // return null;
      // } else {
      //   this.loading = true;
      //   // clear menu update to fetch menu
      //   this.cashedParameters.menu_update = 0;
      // }

      // if any parameter not was changed need to make silent update
      // if menu_update or global_category_id was changed on server side api will return new menu
      // in other cases api will return empty menu
      let cachedData;
      if (branchId) {
        cachedData = this.cache.get(branchId);
        if (cachedData) {
          this.categories = cachedData.categories;
          parameters.menu_update = cachedData.menu_update;
          parameters.global_category_id = cachedData.global_category_id;
          this.loading = false;
        }
      }

      const { categories, menu_update, global_category_id } = await requests.fetchMenu(parameters);

      if (cachedData?.menu_update === menu_update && cachedData?.global_category_id === global_category_id) {
        return;
      }

      if (branchId) {
        this.cache.set(branchId, { categories, menu_update, global_category_id });
      }

      this.categories = categories;

      // Clear selected category if it not was found or missing

      if (this.categoryId && !this.parameters.search) {
        const idx = this.categories.findIndex((category) => category.id === this.categoryId);
        if (!this.categories.length || !~idx) {
          this.setCategory(0);
        }
      }

      this.loading = false;
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'MenuStore fetchMenu' });
    }
  };

  @action public fetchMenuParts = async (category: ICategoryWithProducts | null, branchId: number) => {
    try {
      if (!category || category.items.current_page === category.items.last_page) {
        return;
      }

      for (let page = category.items.current_page + 1; page <= category.items.last_page; page++) {
        this.loading = true;
        const items = await requests.fetchMenuPart(category.id, page, { branch_id: branchId });

        this.categories = this.categories.map((_category) => {
          if (_category.id !== menuStore.category?.id) return _category;
          return {
            ..._category,
            items: { ...items, data: [..._category.items.data, ...items.data] },
          };
        });

        this.loading = false;
      }
    } catch (error: any) {
      logger.error({ error, place: 'MenuStore fetchMenuParts' });
    }
  };

  @action fetchFavoriteProduct = async (customerId: number) => {
    try {
      const responce = await client.get(`/v3/users/${customerId}/favorites`, {
        params: { page: 1, name: this.parameters.search, branch_id: this.parameters.branch_id },
      });

      this.favoriteProducts = responce.data.map((item: IResponseProduct) => productUtils.transformProduct(item));
    } catch (error: any) {
      logger.error({ error, place: 'MenuStore fetchFavoriteProduct' });
    }
  };

  public callFetchMenu = debounce(this.fetchMenu, 400);

  clear() {
    this.loading = false;
    this.favorites = false;
    this.categoryId = undefined;
    this.parameters = {};
    this.categories = [];
    this.favoriteProducts = [];
    this.branch = undefined;
  }
}

const menuStore = new MenuStore();

export { menuStore };
